import React, { useMemo } from 'react';
import { Button } from 'reactstrap';
import { AlertCircle, ArrowRight, CheckCircle, XCircle } from 'react-feather';
import { computeMatchMetrics } from './ProcessingReportHeader';

export default function ProcessingReportOverview({
  mismatchCount,
  onGoToMismatches,
  onGoToCompare,
  reportStatistics,
  processedData,
  processedBackendData,
}) {
  const metrics = useMemo(
    () => computeMatchMetrics(processedData, processedBackendData, reportStatistics),
    [processedData, processedBackendData, reportStatistics]
  );

  const hasIssues =
    (reportStatistics?.skipped_entries ?? 0) > 0 ||
    (reportStatistics?.error_entries ?? 0) > 0 ||
    mismatchCount > 0;

  return (
    <>
      <div className="pr-match-grid">
        {metrics.map(({ key, label, match }) => (
          <div key={key} className={`pr-match ${match ? 'pr-match--ok' : 'pr-match--bad'}`}>
            {match ? <CheckCircle size={16} /> : <XCircle size={16} />}
            <span className="pr-match__label">{label}</span>
            <span className="pr-match__status">{match ? 'Match' : 'Mismatch'}</span>
          </div>
        ))}
      </div>

      <div className="inc-panel pr-overview">
        {mismatchCount > 0 && (
          <div className="pr-alert pr-alert--warning">
            <AlertCircle size={18} />
            <div className="pr-alert__body">
              <strong>{mismatchCount} artist mismatch{mismatchCount === 1 ? '' : 'es'}</strong>
              <span>Need review before invoicing.</span>
            </div>
            <Button color="primary" size="sm" onClick={onGoToMismatches}>
              Review <ArrowRight size={14} className="ms-1" />
            </Button>
          </div>
        )}

        <div className="pr-overview__grid">
          <div className="pr-overview__card">
            <h3 className="pr-overview__heading">Processing</h3>
            <dl className="pr-dl">
              <div className="pr-dl__row">
                <dt>Total rows</dt>
                <dd>{reportStatistics?.total_rows?.toLocaleString() ?? '—'}</dd>
              </div>
              <div className="pr-dl__row">
                <dt>Processed</dt>
                <dd>{reportStatistics?.processed_rows?.toLocaleString() ?? '—'}</dd>
              </div>
              <div className="pr-dl__row">
                <dt>Saved</dt>
                <dd>{reportStatistics?.saved_entries?.toLocaleString() ?? '—'}</dd>
              </div>
              <div className="pr-dl__row">
                <dt>Matched</dt>
                <dd>{reportStatistics?.matched_entries?.toLocaleString() ?? '—'}</dd>
              </div>
            </dl>
          </div>

          <div className="pr-overview__card">
            <h3 className="pr-overview__heading">Issues</h3>
            <dl className="pr-dl">
              <div className="pr-dl__row">
                <dt>Skipped</dt>
                <dd className={reportStatistics?.skipped_entries > 0 ? 'text-danger' : ''}>
                  {reportStatistics?.skipped_entries?.toLocaleString() ?? '0'}
                </dd>
              </div>
              <div className="pr-dl__row">
                <dt>Errors</dt>
                <dd className={reportStatistics?.error_entries > 0 ? 'text-danger' : ''}>
                  {reportStatistics?.error_entries?.toLocaleString() ?? '0'}
                </dd>
              </div>
              <div className="pr-dl__row">
                <dt>Mismatches</dt>
                <dd className={mismatchCount > 0 ? 'text-warning' : ''}>{mismatchCount}</dd>
              </div>
            </dl>
          </div>

          <div className="pr-overview__card pr-overview__card--action">
            <h3 className="pr-overview__heading">Next steps</h3>
            <p className="pr-overview__hint">
              {hasIssues
                ? 'Resolve mismatches and review the backend vs S3 comparison.'
                : 'Data looks consistent. Review the comparison tab to confirm.'}
            </p>
            <Button color="light" size="sm" className="pr-overview__btn" onClick={onGoToCompare}>
              Open comparison <ArrowRight size={14} className="ms-1" />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
